import React, { useState, useRef, useEffect } from 'react';

/**
 * Typeahead Select Component
 * Provides filtering/searching functionality for dropdown selections
 */
export function TypeaheadSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Type to search...',
  disabled = false,
  className = '',
  label,
  required = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const justSelectedRef = useRef(false);

  // Filter options based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    setSelectedIndex(-1);
  }, [inputValue, options]);

  // Update input value when external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    
    // If the input exactly matches an option, call onChange
    const exactMatch = options.find(opt => opt.name.toLowerCase() === newValue.toLowerCase());
    if (exactMatch) {
      onChange(exactMatch.name);
    } else if (newValue === '') {
      onChange('');
    }
  };

  const handleOptionClick = (option) => {
    justSelectedRef.current = true;
    setInputValue(option.name);
    setIsOpen(false);
    onChange(option.name);
    inputRef.current?.blur();
    
    // Reset the flag after a short delay
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredOptions[selectedIndex]) {
          handleOptionClick(filteredOptions[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleBlur = () => {
    // Delay hiding to allow for option clicks
    setTimeout(() => {
      setIsOpen(false);
      
      // Don't revert if we just selected an option
      if (justSelectedRef.current) {
        return;
      }
      
      // If input doesn't match any option exactly, revert to the current value
      const exactMatch = options.find(opt => opt.name.toLowerCase() === inputValue.toLowerCase());
      if (!exactMatch && inputValue !== '') {
        setInputValue(value);
      }
    }, 150);
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium mb-2">
          {label} {required && '*'}
        </label>
      )}
      
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        autoComplete="off"
      />
      
      {isOpen && filteredOptions.length > 0 && (
        <div 
          ref={listRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredOptions.map((option, index) => (
            <div
              key={option.id}
              onClick={() => handleOptionClick(option)}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                index === selectedIndex ? 'bg-blue-100' : ''
              }`}
            >
              {option.name}
            </div>
          ))}
        </div>
      )}
      
      {isOpen && filteredOptions.length === 0 && inputValue.trim() && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg px-3 py-2 text-gray-500">
          No options found
        </div>
      )}
    </div>
  );
}